import { FC, ReactNode, useCallback, useEffect, useMemo } from 'react';
import { Toast } from '../../../interfaces';
import { Icon, Typography, TypographyColor, Link } from '../../shared';
import { ButtonWrapper } from '@tempusfinance/common-ui';
import { removeToast } from '../../../atoms';

import './ToastItem.scss';

/**
 * Parses a string with **bold** markers into React nodes.
 * Text wrapped in ** will be rendered as <strong>.
 */
function parseMessageWithBold(message: string): ReactNode {
  const parts = message.split(/(\*\*.*?\*\*)/g);

  if (parts.length === 1) {
    return message;
  }

  return parts.map((part, index) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={index}>{part.slice(2, -2)}</strong>;
    }
    return <span key={index}>{part}</span>;
  });
}

interface ToastItemProps {
  toast: Toast;
}

const ToastItem: FC<ToastItemProps> = ({ toast }) => {
  const title = useMemo(() => {
    switch (toast.type) {
      case 'success':
        return 'Success';
      case 'error':
        return 'Reverted';
      case 'info':
        return 'Confirming...';
    }
  }, [toast.type]);

  const titleColor = useMemo((): TypographyColor => {
    switch (toast.type) {
      case 'success':
        return 'text-success';
      case 'error':
        return 'text-error';
      case 'info':
        return 'text-supporting';
    }
  }, [toast.type]);

  const onClose = useCallback(() => {
    removeToast(toast);
  }, [toast]);

  useEffect(() => {
    if (toast.type === 'info') {
      return;
    }

    setTimeout(() => {
      onClose();
    }, 10000);
  }, [onClose, toast.type]);

  return (
    <div className="nostra__toastItem">
      <div className="nostra__toastItem__header">
        <Typography
          className="nostra__toastItem__headerTitle"
          variant="table-body-md"
          weight="semi-bold"
          color={titleColor}
        >
          {title}
        </Typography>
        <ButtonWrapper onClick={onClose}>
          <Icon variant="close" size={16} color="--icon-default" />
        </ButtonWrapper>
      </div>
      <Typography variant="body-sm" color="text-primary">
        {parseMessageWithBold(toast.message)}
      </Typography>
      {toast.details && (
        <Typography variant="body-sm" color="text-error">
          {toast.details}
        </Typography>
      )}
      {toast.txId && (
        <Link href={`https://voyager.online/tx/${toast.txId}`}>
          <Typography variant="button-xs" weight="semi-bold" color="text-primary">
            View on explorer
          </Typography>
        </Link>
      )}
      {toast.type !== 'info' && <div className="nostra__toastItem__autoCloseIndicator" data-type={toast.type} />}
    </div>
  );
};
export default ToastItem;
